// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentAccountState } from "@insite/client-framework/Store/Data/Accounts/AccountsSelector";
import { getPageLinkByPageType, useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import approveOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/ApproveOrder";
import deleteOrder from "@insite/client-framework/Store/Pages/OrderApprovalDetails/Handlers/DeleteOrder";
import translate from "@insite/client-framework/Translate";
import { CartModel } from "@insite/client-framework/Types/ApiModels";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import TwoButtonModal, { TwoButtonModalStyles } from "@insite/content-library/Components/TwoButtonModal";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import OverflowMenu, { OverflowMenuProps } from "@insite/mobius/OverflowMenu/OverflowMenu";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import xLoadingOverlay from "@tps/App/Handlers/XNG/LoadingOverlay";
import React, { useState } from "react";

import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    cart: CartModel;
    extendedStyles?: OrderApprovalDetailsActionButtonsStyles;
    // xng-change
    continueShoppingLink: LinkFieldValue;
}

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
    account: getCurrentAccountState(state)?.value,
    cartPageLink: getPageLinkByPageType(state, "CartPage"),
    orderApprovalListPageLink: getPageLinkByPageType(state, "OrderApprovalListPage"),
    homeLink: getPageLinkByPageType(state, "HomePage"),
    // xng-change
    allProductsPageLink: getPageLinkByPageType(state, "AllProductsPage"),
});

const mapDispatchToProps = {
    approveOrder,
    deleteOrder,
    // XNG-Changes: add loading spinner on delete and approve order
    xLoadingOverlay,
};

export interface OrderApprovalDetailsActionButtonsStyles {
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    narrowOverflowMenu?: OverflowMenuProps;
    printButton?: ButtonPresentationProps;
    continueShoppingButton?: ButtonPresentationProps;
    deleteButton?: ButtonPresentationProps;
    approveButton?: ButtonPresentationProps;
    printClickable?: ClickableProps;
    continueShoppingClickable?: ClickableProps;
    deleteClickable?: ClickableProps;
    approveClickable?: ClickableProps;
    twoButtonModalStyles?: TwoButtonModalStyles;
}

export const orderApprovalDetailsActionButtonsStyles: OrderApprovalDetailsActionButtonsStyles = {
    deleteButton: {
        css: css`
            margin-left: 10px;
        `,
        buttonType: "outline",
        variant: "secondary",
    },
    continueShoppingButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    approveButton: {
        css: css`
            margin-left: 10px;
        `,
    },
    printButton: {
        variant: "secondary",
        buttonType: "solid",
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

type Props = OwnProps &
    HasHistory &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext;

const OrderApprovalDetailsActionButtons = ({
    cart,
    history,
    cartPageLink,
    orderApprovalListPageLink,
    homeLink,
    approveOrder,
    deleteOrder,
    extendedStyles,
    account,
    toaster,
    // XNG-Changes
    xLoadingOverlay,
    continueShoppingLink,
    allProductsPageLink,
}: Props) => {
    const [styles] = useState(() => mergeToNew(orderApprovalDetailsActionButtonsStyles, extendedStyles));
    const [confirmationModalIsOpen, setConfirmationModalIsOpen] = React.useState(false);
    const [isDisabledApproveButton, setIsDisabledApproveButton] = useState(false);
    // xng-change
    const linkInfo = useGetLink(continueShoppingLink);

    if (!cart || !account || !orderApprovalListPageLink || !cartPageLink) {
        return null;
    }

    const approvalDisabled = cart.cartLines?.every(cartline => cartline.isRestricted);

    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    const continueShoppingClickHandler = () => {
        // xng-change:
        const redirectLink = linkInfo?.url || allProductsPageLink?.url;

        if (redirectLink) {
            history.push(redirectLink);
        }
    };

    const deleteClickHandler = () => {
        // XNG-Changes
        xLoadingOverlay({ key: "add", id: "delete_order_approval" });
        deleteOrder({
            onSuccess: () => {
                toaster.addToast({ body: translate("Order #{0} Deleted", cart.orderNumber), messageType: "success" });
                history.push(orderApprovalListPageLink.url);
                xLoadingOverlay({ key: "clear", id: "delete_order_approval" });
            },
        });
    };

    const approveClickHandler = () => {
        // XNG-Changes
        xLoadingOverlay({ key: "add", id: "approve_order" });
        setIsDisabledApproveButton(true);
        approveOrder({
            onSuccess: () => {
                history.push(cartPageLink.url);
                xLoadingOverlay({ key: "clear", id: "approve_order" });
            },
            onComplete() {
                setTimeout(() => {
                    setIsDisabledApproveButton(false);
                }, 500);
            },
        });
    };

    const printLabel = translate("Print");
    const deleteLabel = translate("Delete Order");
    const approveLabel = translate("Approve Order");
    const continueShoppingLabel = translate("Continue Shopping");

    return (
        <>
            <Hidden {...styles.menuHiddenContainer}>
                <OverflowMenu position="end" {...styles.narrowOverflowMenu}>
                    <Clickable {...styles.printClickable} onClick={printOrOpenPrintAllModal} aria-label={printLabel}>
                        {printLabel}
                    </Clickable>
                    {account.canApproveOrders ? (
                        <>
                            <Clickable
                                {...styles.deleteClickable}
                                onClick={() => setConfirmationModalIsOpen(true)}
                                aria-label={deleteLabel}
                            >
                                {deleteLabel}
                            </Clickable>
                            <Clickable
                                disabled={approvalDisabled || isDisabledApproveButton}
                                {...styles.approveClickable}
                                onClick={approveClickHandler}
                                aria-label={approveLabel}
                            >
                                {approveLabel}
                            </Clickable>
                        </>
                    ) : (
                        <Clickable
                            {...styles.continueShoppingClickable}
                            onClick={continueShoppingClickHandler}
                            aria-label={continueShoppingLabel}
                        >
                            {continueShoppingLabel}
                        </Clickable>
                    )}
                </OverflowMenu>
            </Hidden>
            <Hidden {...styles.buttonsHiddenContainer}>
                <Button
                    data-test-selector="orderApprovalDetails_print"
                    {...styles.printButton}
                    onClick={printOrOpenPrintAllModal}
                    aria-label={printLabel}
                >
                    {printLabel}
                </Button>
                {account.canApproveOrders ? (
                    <>
                        <Button
                            data-test-selector="orderApprovalDetails_deleteOrder"
                            {...styles.deleteButton}
                            onClick={() => setConfirmationModalIsOpen(true)}
                            aria-label={deleteLabel}
                        >
                            {deleteLabel}
                        </Button>
                        <Button
                            data-test-selector="orderApprovalDetails_approveOrder"
                            {...styles.approveButton}
                            disabled={approvalDisabled || isDisabledApproveButton}
                            onClick={approveClickHandler}
                            aria-label={approveLabel}
                        >
                            {approveLabel}
                        </Button>
                    </>
                ) : (
                    <Button
                        data-test-selector="orderApprovalDetails_continueShopping"
                        {...styles.continueShoppingButton}
                        onClick={continueShoppingClickHandler}
                        aria-label={continueShoppingLabel}
                    >
                        {continueShoppingLabel}
                    </Button>
                )}
            </Hidden>
            <TwoButtonModal
                modalIsOpen={confirmationModalIsOpen}
                headlineText={translate("Delete Order")}
                messageText={translate("Do you want to delete order #{0}?", cart.orderNumber)}
                cancelButtonText={translate("Cancel")}
                submitButtonText={translate("Delete")}
                submitTestSelector="confirmDeleteOrderButton"
                extendedStyles={styles.twoButtonModalStyles}
                onCancel={() => setConfirmationModalIsOpen(false)}
                onSubmit={deleteClickHandler}
            ></TwoButtonModal>
        </>
    );
};

export default connect(
    mapStateToProps,
    mapDispatchToProps,
)(withHistory(withToaster(OrderApprovalDetailsActionButtons)));
